import React, { useMemo } from 'react';
import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import { store } from 'store';

import { dateTimeDisplayFormat } from '~libs/dayjs';
import { Appointment } from '~weekly-planner/lib/common';

import { Row } from '~components/pdf/Row';
import { Section } from '~components/pdf/Section';

interface HookProperties {
  appointment: Appointment | null;
  logo: string;
  company?: any;
}

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: '8pt',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
    width: '100%',
  },
  logo: {
    width: 120,
    height: 'auto',
    flexShrink: 0,
  },
  companyInfo: {
    textAlign: 'left',
    color: '#333',
    flexShrink: 1,
  },
  companyName: {
    fontWeight: 'bold',
    fontSize: '18pt',
    marginBottom: 8,
  },
  companyDetail: {
    marginBottom: 4,
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#000',
  },
  detailsContainer: {
    marginBottom: 15,
  },
  separator: {
    marginTop: 20,
    marginBottom: 20,
    borderTop: '1px solid #ddd',
  },
  row: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  col1: {
    width: '20%',
    fontWeight: 'bold',
  },
  col2: {
    width: '40%',
  },
  label: {
    fontWeight: 'bold',
    color: '#333',
  },
  section: {
    marginBottom: 20,
  },
  sectionTitleStyle: {
    fontWeight: 'bold',
    fontSize: '10pt',
    marginBottom: 10,
    color: '#333',
  },
  footer: {
    position: 'absolute',
    bottom: '30',
    textAlign: 'center',
    fontSize: '8pt',
    color: '#333',
    padding: 10,
  },
});

const getLogoSource = (logo: string) => {
  const token = store.getState().main.login.sessionToken;
  return logo
    ? {
        uri: logo,
        headers: {
          'x-session-token': token,
        },
      }
    : null;
};

const sanitizeTextForPdf = (text: any): string => {
  if (!text || typeof text !== 'string') {
    return 'N/A';
  }

  return (
    text
      // Remove control characters using string filtering
      .split('')
      .filter((char) => {
        const charCode = char.charCodeAt(0);
        // Keep printable characters, newlines (10), carriage returns (13), and tabs (9)
        return charCode >= 32 || charCode === 9 || charCode === 10 || charCode === 13;
      })
      .join('')
      // Remove or replace problematic Unicode characters
      .replace(/[\uFEFF\uFFFE\uFFFF]/g, '')
      // Remove leading and trailing whitespace
      .trim()
  );
};

export const useGeneratePdf = ({ appointment, logo, company }: HookProperties) => {
  const {
    client,
    date,
    start_time,
    end_time,
    service_type,
    user,
    location_override,
    notes,
    location_override: { address, postcode, suburb, state } = {} as any,
    status,
  } = appointment ?? {};

  const sanitizedNotes = sanitizeTextForPdf(notes);

  const serviceType = service_type?.name || 'N/A';
  const careWorker = user?.full_name || 'N/A';
  const clientName = client?.full_name || 'N/A';
  const location = location_override ? `${address}, ${suburb}, ${state} ${postcode}` : 'N/A';
  const {
    abn,
    address: company_address,
    suburb: company_suburb,
    postcode: company_postcode,
    state: company_state,
    name: company_name,
  } = company;

  const logoSource = getLogoSource(logo);
  const logoContent = logoSource ? <Image src={logoSource} style={styles.logo} /> : undefined;

  const currentDateTime = dayjs().format(dateTimeDisplayFormat);
  const sectionStyle = { ...styles, section: styles.section, sectionTitle: styles.sectionTitleStyle };

  const pdfDocument = useMemo(() => {
    return (
      <Document title={`${client?.full_name}`}>
        <Page size={'A4'} style={styles.page}>
          <View style={styles.header}>
            <View style={styles.companyInfo}>
              <Text style={styles.companyName}>{company_name}</Text>
              <Text style={styles.companyDetail}>ABN: {abn}</Text>
              <Text style={styles.companyDetail}>
                {company_address}, {company_suburb}
              </Text>
              <Text style={styles.companyDetail}>
                {company_state} {company_postcode}
              </Text>
            </View>
            {logoContent}
          </View>
          <View style={styles.separator} />
          <Section title="Appointment Details" styles={sectionStyle}>
            <Row label="Date" value={date} styles={styles} />
            <Row label="Reference" value={appointment?.id || 'N/A'} styles={styles} />
          </Section>
          <Section title="Service Details" styles={sectionStyle}>
            <Row label="Service Type" value={serviceType} styles={styles} />
            <Row label="Care Worker" value={careWorker} styles={styles} />
            <Row label="Sch. Start Time" value={start_time} styles={styles} />
            <Row label="Sch. End Time" value={end_time} styles={styles} />
          </Section>
          <Section title="Location & Client Details" styles={sectionStyle}>
            <Row label="Location" value={location} styles={styles} />
            <Row label="Client/s" value={clientName} styles={styles} />
            <Row label="Notes" value={sanitizedNotes} styles={styles} />
          </Section>
          <Section title="Status" styles={sectionStyle}>
            <Row label="Status" value={status?.name} styles={styles} />
          </Section>

          <Text fixed style={styles.footer}>
            Printed on {currentDateTime}
          </Text>
        </Page>
      </Document>
    );
  }, [appointment, company, logo]);

  return pdfDocument;
};

export default useGeneratePdf;
